/**
 * Reptilian Agent - Game State (Headless, no GUI)
 * Idle clicker game state definition
 */

import type { DailyMission } from './data/missions.js';

// Producer (generator) ownership data
export interface ProducerOwned {
  count: number;
}

// Active boost effect
export interface ActiveBoost {
  type: 'cps2x' | 'tap3x' | 'tap5x' | 'cps3x' | 'tap10x' | 'cps5x' | 'autoClick';
  multiplier: number;
  endsAt: number; // timestamp
}

// Golden event on screen
export interface GoldenEvent {
  id: string;
  type: 'intel_leak' | 'agent_frenzy' | 'money_launder' | 'facility_boost' | 'supply_drop';
  appearedAt: number;
  expiresAt: number;
}

// Achievement status
export interface AchievementStatus {
  unlocked: boolean;
  unlockedAt?: number;
}

// Prestige 1 upgrade levels
export type PrestigeUpgradeId =
  | 'galaxyMultiplier'
  | 'infiltrationAccel'
  | 'tapEnhance'
  | 'offlineExpand'
  | 'offlineEfficiency'
  | 'agentDiscount'
  | 'autoClick'
  | 'goldenLuck'
  | 'comboKeep'
  | 'quickStart'
  | 'infiltrationMastery'
  | 'agentNetwork'
  | 'quickInfiltration'
  | 'criticalChance'
  | 'criticalDamage';

// Prestige 2 upgrade IDs
export type Prestige2UpgradeId =
  | 'galaxySpeed'
  | 'autoPrestige'
  | 'permanentAgents'
  | 'dimensionAccel'
  | 'galaxyMemory'
  | 'spacetimeWarp'
  | 'galaxyAutomate';

// Conquest state per country
export interface ConquestCountryState {
  invested: number;
  conquered: boolean;
  facilities: [number, number, number];
}

export interface ConquestState {
  activeCountry: string | null; // currently infiltrating country
  countries: Record<string, ConquestCountryState>;
  endingUnlocked: boolean;
}

export interface GameState {
  // Primary currency
  ip: number; // Infiltration Points (current)
  totalIpEarned: number; // total IP ever earned (for prestige calc)
  totalIpThisRun: number; // total IP earned in current run

  // Secondary currencies
  gc: number; // Galactic Credits
  totalGcEarned: number;
  cores: number; // Galactic Cores (prestige 2)
  totalCoresEarned: number;
  classifiedDocs: number; // bonus currency
  totalDocsUsed: number;

  // Tap
  tapCount: number;
  baseTapPower: number; // base IP per tap
  comboCount: number;
  comboLastTapAt: number; // timestamp
  maxCombo: number;

  // Producers (12 tiers)
  producers: Record<string, ProducerOwned>;

  // Click upgrades purchased (indices)
  clickUpgradesBought: number[];

  // Special upgrades purchased (IDs)
  specialUpgradesBought: string[];

  // Prestige
  prestigeCount: number; // total prestige 1 count
  prestigeUpgrades: Record<PrestigeUpgradeId, number>; // upgrade levels

  // Prestige 2
  prestige2Count: number;
  prestige2Upgrades: Record<Prestige2UpgradeId, number>;

  // Achievements
  achievements: Record<string, AchievementStatus>;

  // Active boosts
  activeBoosts: ActiveBoost[];

  // Prestige boost (galactic amplifier) — purchased but not yet consumed
  prestigeBoostActive: boolean;

  // Golden events
  currentGoldenEvent: GoldenEvent | null;
  goldenEventsTouched: number;
  lastGoldenEventAt: number;
  nextGoldenEventAt: number;

  // Offline
  lastOnlineAt: number; // timestamp
  offlineIpPending: number; // pending offline IP to show in popup

  // Stats
  totalPlayTimeMs: number;
  sessionStartAt: number;
  totalAdsWatched: number;
  gameStartedAt: number;

  // Ad cooldowns
  adCooldowns: Record<string, number>; // adType -> available timestamp

  // Daily missions
  dailyMissions: DailyMission[];
  dailyMissionDate: string; // YYYY-MM-DD UTC
  dailyMissionAllClaimed: boolean;

  // Rank (promoted grade, not auto-calculated)
  rank: string; // 'E' | 'D' | 'C' | 'B' | 'A' | 'S' | 'SS' | 'SSS'

  // Conquest
  conquest: ConquestState;

  // Settings (saved but not game logic)
  bgmVolume: number;
  sfxVolume: number;
}

export function createInitialState(): GameState {
  const now = Date.now();
  return {
    ip: 0,
    totalIpEarned: 0,
    totalIpThisRun: 0,

    gc: 0,
    totalGcEarned: 0,
    cores: 0,
    totalCoresEarned: 0,
    classifiedDocs: 0,
    totalDocsUsed: 0,

    tapCount: 0,
    baseTapPower: 1,
    comboCount: 0,
    comboLastTapAt: 0,
    maxCombo: 0,

    producers: {},

    clickUpgradesBought: [],
    specialUpgradesBought: [],

    prestigeCount: 0,
    prestigeUpgrades: {
      galaxyMultiplier: 0,
      infiltrationAccel: 0,
      tapEnhance: 0,
      offlineExpand: 0,
      offlineEfficiency: 0,
      agentDiscount: 0,
      autoClick: 0,
      goldenLuck: 0,
      comboKeep: 0,
      quickStart: 0,
      infiltrationMastery: 0,
      agentNetwork: 0,
      quickInfiltration: 0,
      criticalChance: 0,
      criticalDamage: 0,
    },

    prestige2Count: 0,
    prestige2Upgrades: {
      galaxySpeed: 0,
      autoPrestige: 0,
      permanentAgents: 0,
      dimensionAccel: 0,
      galaxyMemory: 0,
      spacetimeWarp: 0,
      galaxyAutomate: 0,
    },

    achievements: {},
    activeBoosts: [],
    prestigeBoostActive: false,

    currentGoldenEvent: null,
    goldenEventsTouched: 0,
    lastGoldenEventAt: now,
    nextGoldenEventAt: now + 300_000 + Math.random() * 600_000,

    lastOnlineAt: now,
    offlineIpPending: 0,

    totalPlayTimeMs: 0,
    sessionStartAt: now,
    totalAdsWatched: 0,
    gameStartedAt: now,

    adCooldowns: {},

    dailyMissions: [],
    dailyMissionDate: '',
    dailyMissionAllClaimed: false,

    rank: 'E',

    conquest: {
      activeCountry: null,
      countries: {},
      endingUnlocked: false,
    },

    bgmVolume: 50,
    sfxVolume: 70,
  };
}
